// Alert trigger engine — reactive watchers that consume GPS + plan +
// terrain state and push alerts into the alertStore. Hosted as a
// React hook (`useAlertTriggers`) so triggers tear down cleanly with
// the host component and React's effect lifecycle handles GPS-fix
// re-evaluation cadence.
//
// Boss feedback 2026-05-10. Three triggers wired:
//
//   1. TERRAIN_AHEAD     AGL drops below threshold (configurable;
//                        default 500 ft for warning, 200 ft critical).
//   2. APPROACHING_RWY   GPS comes within 3 NM of the next airport
//                        in the active flight plan.
//   3. DESTINATION_FREQ  Within 10 NM of the destination, surface
//                        the published ATIS / CTAF frequency.
//
// Each trigger uses a stable id so the store dedupes within its
// cooldown window (default 30s). The 'critical' AGL alert uses a
// shorter dedupe + longer ttl so a sustained low-altitude flight
// keeps the warning visible.

import { useEffect, useRef } from 'react';
import { useGpsStore } from '../stores/gpsStore';
import { usePlanStore } from '../stores/planStore';
import { useAlertStore } from '../stores/alertStore';
import { useLayerVisibilityStore } from '../stores/layerVisibilityStore';
import { useTerrainAdvisorStore } from '../stores/terrainAdvisorStore';
import { useAirborneStore } from '../stores/airborneStore';
import { useIncidentStore } from '../stores/incidentStore';
import { isLikelyGround } from './airborneDetector';
import { getCachedOverlay } from '../map/overlayRuntime';
import { useIsDesktop } from '../shell/useBreakpoint';
import { greatCircleNm, pointInGeoJsonPolygon } from './geo';
import { useSmoothedAltFt } from './altitudeSource';
import { pickMostRelevantIhog, pickIhogContact, type IhogZone } from './ihogProximity';

const MPS_TO_KT = 1.94384;

const M_TO_FT = 3.28084;

export function useAlertTriggers(opts?: {
  /** Override the AGL warning threshold (ft). Default 500. */
  aglWarnFt?: number;
  /** Override the AGL critical threshold (ft). Default 200. */
  aglCritFt?: number;
  /** Approach radius for runway alerts (NM). Default 3. */
  rwyApproachNm?: number;
  /** Destination radius for freq alerts (NM). Default 10. */
  destFreqNm?: number;
  /** Async-resolved terrain elevation at the GPS fix (ft MSL). */
  terrainElevFt: number | null;
}) {
  const aglWarn = opts?.aglWarnFt ?? 500;
  const aglCrit = opts?.aglCritFt ?? 200;
  const rwyNm = opts?.rwyApproachNm ?? 3;
  const destNm = opts?.destFreqNm ?? 10;
  const terrain = opts?.terrainElevFt ?? null;

  const fix = useGpsStore((s) => s.fix);
  const route = usePlanStore((s) => s.route);
  const push = useAlertStore((s) => s.push);

  // Best-available altitude (baro → smoothed GPS → raw). Terrain alerts
  // refuse to fire when the reading is not trustworthy yet (first-fix
  // warmup), so a noisy initial sample can't trigger a false 'below
  // 200 ft AGL' on power-on.
  const altReading = useSmoothedAltFt();

  // Velocity gate, shared across terrain-advisor triggers. We only
  // alert when GPS reads above the activation speed; below that
  // threshold (parked, hovering, taxiing) the warning is suppressed.
  //
  // Tricky bit: many browsers / devices leave `pos.coords.speed` as
  // `null` when stationary instead of reporting 0 m/s. If we only
  // gated on `speedMps`, a parked phone with null-speed would always
  // be 'not stationary' and the alert would fire — exactly the bug
  // boss reported.
  //
  // Robust fix: derive our own ground speed by diffing the current
  // fix against the previous one we saw. If either source (browser-
  // reported OR derived) reads above the threshold we consider the
  // pilot moving. If the BROWSER explicitly reports a speed below the
  // threshold, that wins (it's authoritative). Only when both sources
  // come up null do we leave the gate open (planning / sim mode).
  const activationKt    = useTerrainAdvisorStore((s) => s.activationSpeedKt);
  const alertsEnabled   = useTerrainAdvisorStore((s) => s.alertsEnabled);
  const corridorMode    = useTerrainAdvisorStore((s) => s.corridorMode);
  const airborneState   = useAirborneStore((s) => s.state);
  const prevFixRef = useRef<{ lat: number; lon: number; t: number } | null>(null);
  const derivedKt = (() => {
    if (!fix) return null;
    const prev = prevFixRef.current;
    if (prev && fix.timestamp > prev.t) {
      const dtSec = (fix.timestamp - prev.t) / 1000;
      // Ignore very small dt (jitter) and very large dt (woke up).
      if (dtSec > 0.5 && dtSec < 120) {
        const dNm = greatCircleNm(prev.lat, prev.lon, fix.lat, fix.lon);
        const kt = (dNm / dtSec) * 3600;
        if (Number.isFinite(kt) && kt >= 0) return kt;
      }
    }
    return null;
  })();
  // Keep prev fix in sync. We do this in an effect so render is pure,
  // but read derivedKt above by indexing into the ref BEFORE the
  // effect overwrites it.
  useEffect(() => {
    if (fix) prevFixRef.current = { lat: fix.lat, lon: fix.lon, t: fix.timestamp };
  }, [fix?.lat, fix?.lon, fix?.timestamp, fix]);
  const liveKt = fix?.speedMps != null ? fix.speedMps * MPS_TO_KT : null;
  // Decide stationarity:
  //  - If browser reports any speed, trust it.
  //  - Else fall back to derived (consecutive-fix diff) speed.
  //  - If both are null AND we have a live GPS fix, assume
  //    stationary (conservative: a parked phone shouldn't fire
  //    a TAWS warning on its first lock-in). A few seconds later
  //    the derived path will take over.
  //  - If there's no fix at all (planning / sim), leave the gate
  //    open — the trigger short-circuits earlier anyway.
  let stationary: boolean;
  if (liveKt != null) {
    stationary = liveKt < activationKt;
  } else if (derivedKt != null) {
    stationary = derivedKt < activationKt;
  } else if (fix != null) {
    // Have a fix but no speed signal yet — treat as stationary so
    // the advisor doesn't fire spuriously on first GPS lock.
    stationary = true;
  } else {
    stationary = false;
  }

  // -- Trigger 1: TERRAIN_AHEAD on AGL crossing thresholds.
  useEffect(() => {
    // Kill switch: user turned off terrain alerts entirely.
    if (!alertsEnabled) return;
    // Kill switch: user explicitly hid the TAWS overlay (they meant it).
    if (corridorMode === 'hide') return;
    // Motion gate: only fire when we're armed or airborne.
    // 'ground' and likely-ground 'unknown' both suppress alerts — this
    // is the core fix for the car-in-mountains false-alert bug.
    if (airborneState === 'ground') return;
    if (airborneState === 'unknown' && isLikelyGround()) return;
    if (stationary) return;       // gated: no advisory on the ground
    if (!fix || terrain == null) return;
    // Use the smoothed / baro-preferred altitude instead of raw GPS.
    // If we don't have a trustworthy reading yet (smoother is still
    // collecting samples), suppress the alert rather than risk a
    // false fire on a transient low sample.
    if (altReading.altFt == null || !altReading.trustworthy) return;
    const aglFt = altReading.altFt - terrain;
    if (!Number.isFinite(aglFt)) return;
    if (aglFt < aglCrit) {
      push({
        id: 'terrain-critical',
        severity: 'critical',
        title: 'TERRAIN AHEAD',
        body: `Below ${aglCrit} ft AGL (${aglFt.toFixed(0)} ft). Climb or turn.`,
        ttlMs: 8000,
        dedupeMs: 60_000,
      });
    } else if (aglFt < aglWarn) {
      push({
        id: 'terrain-warning',
        severity: 'warning',
        title: 'Low terrain clearance',
        body: `${aglFt.toFixed(0)} ft AGL. Watch terrain ahead.`,
        ttlMs: 6000,
        dedupeMs: 5 * 60_000,
      });
    }
  }, [fix?.lat, fix?.lon, altReading.altFt, altReading.trustworthy, terrain, aglCrit, aglWarn, push, fix, stationary, alertsEnabled, corridorMode, airborneState]);

  // -- Trigger 2: APPROACHING_RWY when GPS comes within rwyNm of the
  // next airport waypoint in the route.
  //
  // Mobile-only and sticky (boss 2026-05-21 18:31 PDT): desktop sees no
  // benefit — nobody flies with a laptop — and the in-cockpit pilot wants
  // the frequency card to stay up until they tap it away rather than
  // auto-dismissing 9 s later.
  const isDesktop = useIsDesktop();
  useEffect(() => {
    if (isDesktop) return;
    if (!fix || !route || route.length === 0) return;
    for (const pt of route) {
      if (pt.kind !== 'airport' || !pt.ident) continue;
      const dNm = greatCircleNm(fix.lat, fix.lon, pt.lat, pt.lon);
      if (dNm <= rwyNm && dNm > 0.5) {  // skip 'on the field' moments
        push({
          id: `approaching-${pt.ident}`,
          severity: 'info',
          title: `Approaching ${pt.ident}`,
          body: `${dNm.toFixed(1)} NM. Set up for traffic pattern or approach.`,
          ttlMs: 0,                // sticky — user dismisses
          dedupeMs: 5 * 60_000,    // don't re-alert the same field for 5 min
        });
      }
    }
  }, [fix?.lat, fix?.lon, route, rwyNm, push, fix, isDesktop]);

  // -- Trigger 3: DESTINATION_FREQ — within destNm of the LAST airport
  // waypoint, surface its published ATIS / CTAF frequency. We assume
  // the destination's wx freq has been hydrated into pt.wxFreq when
  // available (see planStore enrichment); the alert just shows what's
  // there. If no wxFreq is on the waypoint it falls back to a generic
  // 'tune CTAF' message.
  //
  // Mobile-only and sticky (same rationale as Trigger 2).
  useEffect(() => {
    if (isDesktop) return;
    if (!fix || !route || route.length === 0) return;
    let destIdx = -1;
    for (let i = route.length - 1; i >= 0; i--) {
      if (route[i].kind === 'airport' && route[i].ident) { destIdx = i; break; }
    }
    if (destIdx < 0) return;
    const dest = route[destIdx];
    const dNm = greatCircleNm(fix.lat, fix.lon, dest.lat, dest.lon);
    if (dNm > destNm || dNm <= 0.5) return;
    const wxFreq = (dest as { wxFreq?: string }).wxFreq;
    push({
      id: `dest-freq-${dest.ident}`,
      severity: 'info',
      title: `${dest.ident} — destination wx`,
      body: wxFreq
        ? `ATIS / CTAF ${wxFreq}. ${dNm.toFixed(1)} NM out.`
        : `${dNm.toFixed(1)} NM out. Tune CTAF / unicom.`,
      ttlMs: 0,                  // sticky — user dismisses
      dedupeMs: 10 * 60_000,
    });
  }, [fix?.lat, fix?.lon, route, destNm, push, fix, isDesktop]);

  // -- Trigger 4: DISPATCH_BOUNDARY_CROSS — alert when the GPS fix
  // enters a different NIFC dispatch zone than the last fix. Only
  // fires when the user has the dispatch-zone layer toggled on; the
  // overlay's cached GeoJSON is the source of truth.
  //
  // Implementation: ray-cast PIP across the cached FeatureCollection
  // on every GPS fix, compare DispUnitID to the previous match, push
  // an alert on change. First-fix match doesn't alert (so toggling
  // the layer on while parked doesn't immediately fire).
  const showDispatch = useLayerVisibilityStore((s) => s.layers.dispatch);
  const lastDispatchId = useRef<string | null>(null);
  const dispatchInitialized = useRef<boolean>(false);
  useEffect(() => {
    if (!showDispatch) {
      // Layer was turned off — reset tracking so re-enabling doesn't
      // immediately fire an alert with stale state.
      lastDispatchId.current = null;
      dispatchInitialized.current = false;
      return;
    }
    if (!fix) return;
    const fc = getCachedOverlay('dispatch');
    if (!fc || !fc.features || fc.features.length === 0) return;
    let matched: { id: string; name: string; phone: string | null; gacc: string | null } | null = null;
    for (const f of fc.features) {
      if (pointInGeoJsonPolygon(fix.lon, fix.lat, f.geometry as any)) {
        const p = (f.properties ?? {}) as Record<string, unknown>;
        matched = {
          id: String(p.DispUnitID ?? p.OBJECTID ?? p.DispName ?? '?'),
          name: String(p.DispName ?? p.DispLocation ?? 'Unknown dispatch'),
          phone: p.ContactPhone != null ? String(p.ContactPhone) : null,
          gacc: p.GACCAbbreviation != null ? String(p.GACCAbbreviation) : null,
        };
        break;
      }
    }
    const newId = matched?.id ?? null;
    if (!dispatchInitialized.current) {
      // First evaluation — record but don't alert.
      lastDispatchId.current = newId;
      dispatchInitialized.current = true;
      return;
    }
    if (newId === lastDispatchId.current) return;
    // Boundary crossed (or moved out of all zones).
    lastDispatchId.current = newId;
    if (matched) {
      const phoneFmt = matched.phone && matched.phone.length === 10
        ? `${matched.phone.slice(0, 3)}-${matched.phone.slice(3, 6)}-${matched.phone.slice(6)}`
        : matched.phone;
      push({
        id: `dispatch-${matched.id}`,
        severity: 'info',
        title: `Now in ${matched.name}`,
        body: matched.gacc
          ? `${matched.gacc} · contact ${phoneFmt ?? 'dispatch'}`
          : `Contact ${phoneFmt ?? 'dispatch'}`,
        ttlMs: 12_000,
        dedupeMs: 15 * 60_000,
      });
    } else {
      // Left all zones (e.g. flew offshore).
      push({
        id: 'dispatch-out',
        severity: 'info',
        title: 'Outside any dispatch zone',
        body: 'No dispatch-zone coverage at current position.',
        ttlMs: 8000,
        dedupeMs: 30 * 60_000,
      });
    }
  }, [showDispatch, fix?.lat, fix?.lon, push]);

  // -- Trigger 5: FTA_APPROACH — Fire Traffic Area entry alert
  // (12 NM contact ring + 7 NM exclusion ring around active fire
  // incidents). Radii defaults from IHOG (Interagency Helicopter
  // Operations Guide); per-incident overrides honored via the resolve
  // helper in ihogProximity.ts. User-facing strings use "FTA" /
  // "Fire Traffic Area" per industry convention; internal variable
  // names keep `ihog` because the source document is IHOG.
  //
  // State machine per incident: track the last zone we observed so we
  // only fire on transitions (outside→contact, contact→exclusion,
  // outside→exclusion). Re-entering the same zone after leaving it
  // re-arms the alert. First evaluation per incident is silent so an
  // app cold-start at the helibase doesn't immediately scream
  // EXCLUSION at the pilot.
  const incidents = useIncidentStore((s) => s.list);
  const lastIhogZoneById = useRef<Map<string, IhogZone>>(new Map());
  useEffect(() => {
    if (!fix) return;
    if (!incidents || incidents.length === 0) {
      lastIhogZoneById.current.clear();
      return;
    }

    const best = pickMostRelevantIhog({ lat: fix.lat, lon: fix.lon }, incidents);

    // For incidents the pilot left entirely, drop them from the
    // tracking map so re-entry later fires the alert again.
    const seenIds = new Set<string>();
    for (const inc of incidents) {
      if (inc.status !== 'active' || inc.latitude == null || inc.longitude == null) continue;
      seenIds.add(inc.id);
    }
    for (const id of Array.from(lastIhogZoneById.current.keys())) {
      if (!seenIds.has(id)) lastIhogZoneById.current.delete(id);
    }

    if (!best) {
      // Outside every active incident's contact ring. Reset every
      // tracked incident to 'outside' so re-entering re-fires.
      for (const id of Array.from(lastIhogZoneById.current.keys())) {
        lastIhogZoneById.current.set(id, 'outside');
      }
      return;
    }

    const { incident, classification } = best;
    const prevZone = lastIhogZoneById.current.get(incident.id) ?? null;

    // Don't alert on the first evaluation for an incident — just record
    // the state. This prevents a false fire when the app starts up at
    // the helibase inside a TFR.
    if (prevZone == null) {
      lastIhogZoneById.current.set(incident.id, classification.zone);
      return;
    }

    // Same zone as before — nothing to alert.
    if (prevZone === classification.zone) return;

    // Transitioned. Record + fire.
    lastIhogZoneById.current.set(incident.id, classification.zone);

    const contact = pickIhogContact(incident.aviation_contacts);
    const contactLabel = contact
      ? `${contact.role || contact.customLabel || 'contact'}: ${contact.name ?? ''}${contact.phone ? ' · ' + contact.phone : ''}`.trim()
      : 'Air Attack / ATGS (no contact on file)';

    if (classification.zone === 'exclusion') {
      push({
        id: `ihog-exclusion-${incident.id}`,
        severity: 'critical',
        title: `FTA EXCLUSION — ${incident.name}`,
        body: `Inside ${classification.exclusionNm.toFixed(0)} NM ring (${classification.distanceNm.toFixed(1)} NM). Do NOT approach without clearance from ${contactLabel}.`,
        ttlMs: 12_000,
        dedupeMs: 2 * 60_000,
      });
    } else if (classification.zone === 'contact') {
      push({
        id: `ihog-contact-${incident.id}`,
        severity: 'warning',
        title: `FTA Contact — ${incident.name}`,
        body: `Inside ${classification.contactNm.toFixed(0)} NM ring (${classification.distanceNm.toFixed(1)} NM). Initiate radio contact — ${contactLabel}.`,
        ttlMs: 10_000,
        dedupeMs: 5 * 60_000,
      });
    }
  }, [fix?.lat, fix?.lon, incidents, push, fix]);

  // -- Trigger 6: CLASS_BCD_AIRSPACE -- alert when GPS enters Class B, C,
  // or D airspace boundaries.  Uses the cached classBCD overlay GeoJSON
  // (same PIP approach as the dispatch-boundary trigger). Only fires when
  // the classBCD layer is ON and has data loaded; otherwise the overlay
  // cache is empty and we can't do the containment check.
  //
  // Severity by class:
  //   Class B: critical  — requires ATC clearance to enter
  //   Class C: warning   — requires two-way radio contact
  //   Class D: info      — two-way contact required
  //
  // First-evaluation-silent (prevents app-cold-start false fires when
  // parked at a towered airport). Re-arms on exit so the pilot gets a
  // fresh alert when they re-enter.
  const showClassBCD = useLayerVisibilityStore((s) => s.layers.classBCD);
  const lastBcdById = useRef<Map<string, string>>(new Map());
  const bcdInitialized = useRef<boolean>(false);
  useEffect(() => {
    if (!showClassBCD) {
      lastBcdById.current.clear();
      bcdInitialized.current = false;
      return;
    }
    if (!fix) return;
    const fc = getCachedOverlay('classBCD');
    if (!fc || !fc.features || fc.features.length === 0) return;

    // Find every B/C/D polygon containing the current GPS fix.
    const inside = new Set<string>();
    for (const f of fc.features) {
      if (!pointInGeoJsonPolygon(fix.lon, fix.lat, f.geometry as any)) continue;
      const p = (f.properties ?? {}) as Record<string, unknown>;
      const cls = String(p.CLASS ?? '');
      if (!cls || !['B', 'C', 'D'].includes(cls)) continue;
      const name = String(p.NAME_TXT ?? p.IDENT_TXT ?? `Class ${cls}`);
      // Key by OBJECTID so each airspace sector is tracked independently.
      const featureKey = String(p.OBJECTID ?? name);
      inside.add(`${featureKey}:${cls}:${name}`);
    }

    if (!bcdInitialized.current) {
      // First evaluation — record without alerting.
      for (const token of inside) lastBcdById.current.set(token, 'inside');
      bcdInitialized.current = true;
      return;
    }

    // Alert on new entries (not already tracked as 'inside').
    for (const token of inside) {
      if (lastBcdById.current.get(token) === 'inside') continue;
      lastBcdById.current.set(token, 'inside');

      const [, cls, name] = token.split(':');
      const clsMessages: Record<string, { severity: 'critical' | 'warning' | 'info'; title: string; body: string }> = {
        B: {
          severity: 'critical',
          title: `Entering Class B — ${name}`,
          body: 'ATC clearance required. Contact approach control before entering.',
        },
        C: {
          severity: 'warning',
          title: `Entering Class C — ${name}`,
          body: 'Two-way radio contact with approach required. Transponder mode C.',
        },
        D: {
          severity: 'info',
          title: `Entering Class D — ${name}`,
          body: 'Two-way radio contact with tower required before entering.',
        },
      };
      const msg = clsMessages[cls];
      if (!msg) continue;
      push({
        id: `classBCD-enter-${token}`,
        severity: msg.severity,
        title: msg.title,
        body: msg.body,
        ttlMs: 10_000,
        dedupeMs: 10 * 60_000,
      });
    }

    // Mark exited airspaces so re-entry re-arms the alert.
    for (const token of Array.from(lastBcdById.current.keys())) {
      if (!inside.has(token)) lastBcdById.current.delete(token);
    }
  }, [showClassBCD, fix?.lat, fix?.lon, push, fix]);
}
